import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { PowerCalendarArgs } from 'ember-power-calendar/components/power-calendar';
import PowerCalendar from 'ember-power-calendar/components/power-calendar';
// eslint-disable-next-line import-x/no-named-as-default
import powerCalendarFormatDate from 'ember-power-calendar/helpers/power-calendar-format-date';
import MarketSmartInsightsCalendarDaysSmartInsights from 'lumen/components/market/smart-insights/calendar/days-smart-insights';
import MarketSmartInsightsCalendarFooterSmartInsights from 'lumen/components/market/smart-insights/calendar/footer-smart-insights';
import type { SmartInsightDay } from 'lumen/models';
import scrollPosition from 'lumen/modifiers/scroll-position';

import styles from './calendar-smart-insights.module.css';

interface MarketSmartInsightsCalendarCalendarSmartInsightsSignature {
  Args: {
    insightCountsByStayDate: Record<string, number>;
    insightsByStayDate: Record<string, SmartInsightDay[]>;
    maxInsightEntriesPerDay: number;
    selectSmartInsightsDay: (stayDate: string) => void;
  };
}

export default class MarketSmartInsightsCalendarCalendarSmartInsights extends Component<MarketSmartInsightsCalendarCalendarSmartInsightsSignature> {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare smartInsights: Services['smart-insights'];
  @tracked showBottomOverlay: boolean = true;
  @tracked showTopOverlay: boolean = false;

  get calendarMonths(): PowerCalendarArgs['center'][] {
    const now = this.hotelAndMarketService.getLocalTime();
    const monthsMap = new Map();

    for (let i = 0; i <= this.smartInsights.leadtime; i++) {
      const dayMonth = now.add(i, 'day').startOf('day');
      monthsMap.set(dayMonth.format('YYYY-MM'), dayMonth);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Array.from(monthsMap.values());
  }

  @action handleOnScrollEvent(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const { scrollTop, clientHeight, scrollHeight } = target;
    this.showTopOverlay = scrollTop > 0;
    this.showBottomOverlay = scrollTop + clientHeight < scrollHeight;
  }

  <template>
    <div
      class="portfolio-smart-insights-calendar flex h-full flex-col justify-between"
    >
      <div class={{styles.content}}>
        {{#if this.showTopOverlay}}
          <div class={{styles.top-overlay}}>
          </div>
        {{/if}}

        <div
          class="{{styles.calendar}}
            flex h-full flex-grow-1 flex-col overflow-auto"
          {{scrollPosition this.handleOnScrollEvent}}
        >
          {{#each this.calendarMonths as |calendarMonth|}}
            <div class="mb-600">
              <div class="flex items-center justify-center">
                <PowerCalendar
                  @center={{calendarMonth}}
                  @daysComponent={{MarketSmartInsightsCalendarDaysSmartInsights}}
                  @locale={{this.hotelAndMarketService.user.locale}}
                  class="demo-calendar-medium portfolio-smart-insights-calendar"
                  data-test-id="portfolio-smart-insight-calendar"
                  as |calendar|
                >
                  <calendar.Nav>
                    <div class="mb-200 flex gap-200">
                      <div
                        class="smart-insights-calendar__year paragraph-400--regular"
                      >
                        {{powerCalendarFormatDate
                          calendar.center
                          "YYYY"
                          locale=calendar.locale
                        }}
                      </div>
                      <h3 class="smart-insights-calendar__month">
                        {{powerCalendarFormatDate
                          calendar.center
                          "MMMM"
                          locale=calendar.locale
                        }}
                      </h3>
                    </div>
                  </calendar.Nav>
                  <calendar.Days
                    {{! @glint-expect-error: Incorrect type }}
                    @insightCountsByStayDate={{@insightCountsByStayDate}}
                    @insightsByStayDate={{@insightsByStayDate}}
                    @maxInsightEntriesPerDay={{@maxInsightEntriesPerDay}}
                    @selectSmartInsightsDay={{@selectSmartInsightsDay}}
                    @showDaysAround={{false}}
                    @startOfWeek={{this.dates.startOfWeek}}
                    @weekdayFormat="min"
                  />
                </PowerCalendar>
              </div>
            </div>
          {{/each}}
        </div>

        {{#if this.showBottomOverlay}}
          <div class={{styles.bottom-overlay}}> </div>
        {{/if}}
      </div>

      <div class="{{styles.footer}} flex-shrink-0 shadow-neutral-100">
        <MarketSmartInsightsCalendarFooterSmartInsights />
      </div>
    </div>
  </template>
}
